import { useState, useCallback } from 'react';
import { ImagePlus, Upload, FolderOpen } from 'lucide-react';

interface ImageDropZoneProps {
  onImageDrop: (files: FileList) => void;
  onImageClick: () => void;
  isEmpty: boolean;
}

export const ImageDropZone = ({ onImageDrop, onImageClick, isEmpty }: ImageDropZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        onImageDrop(files);
      }
    },
    [onImageDrop]
  );

  if (isEmpty) {
    return (
      <div
        className={`drop-zone ${isDragging ? 'drop-zone-active' : ''}`}
        onClick={onImageClick}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-colors ${
          isDragging
            ? 'bg-coral-500 text-white'
            : 'bg-coral-100 text-coral-500'
        }`}>
          {isDragging ? (
            <FolderOpen size={32} />
          ) : (
            <ImagePlus size={32} />
          )}
        </div>
        <div className="text-center">
          <p className="font-display font-semibold text-warm-700">
            {isDragging ? 'ここにドロップ' : '画像をアップロード'}
          </p>
          <p className="text-sm text-warm-400 mt-1">
            クリックまたはドラッグ&ドロップ
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`drop-zone drop-zone-compact ${isDragging ? 'drop-zone-active' : ''}`}
      onClick={onImageClick}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
          isDragging
            ? 'bg-coral-500 text-white'
            : 'bg-coral-100 text-coral-500'
        }`}>
          {isDragging ? (
            <FolderOpen size={20} />
          ) : (
            <Upload size={20} />
          )}
        </div>
        <div className="text-left">
          <p className="text-sm font-medium text-warm-600">
            {isDragging ? '追加でドロップ' : '画像を追加'}
          </p>
          <p className="text-xs text-warm-400">
            クリックまたはドラッグ&ドロップ
          </p>
        </div>
      </div>
    </div>
  );
};
